import { type FC, type KeyboardEvent, useEffect, useState } from 'react';
import { cn } from '@components/lib/utils';
import { Show } from '@components/Show';
import Tooltip from '@components/Tooltip';
import { Button } from '@components/ui/button';
import type { EvalFinding } from '@queries/evalRuns';
import { ChevronDown, ChevronUp, CircleCheck, CircleX, CornerDownLeft, Radar, Undo2 } from 'lucide-react';

import { gradeSegmentClasses, quietInputClasses } from '../classes';
import { RESOLUTION_LABEL } from '../constants';
import { TIER_BY_SEVERITY, TIER_ICON, TIER_LABEL, tierClasses } from '../tiers';
import type { EvalFindingGrade, EvalFindingResolution } from '../types';
import { GRADE_OPTIONS } from '../types';
import { formatDateTime } from '../utils/formatDateTime';
import { lensDisplayLabel, lensHint } from '../utils/lenses';
import { renderTextWithBlockRefs, replaceBlockRefsWithNames } from '../utils/renderBlockRefs';

interface FindingCardProps {
  finding: EvalFinding;
  /** True when the referenced block is currently focused in the contract viewer. */
  isActive: boolean;
  /** Collapsed rows are the default; the list owns which card is open. */
  isExpanded: boolean;
  isGrading: boolean;
  isResolving?: boolean;
  /** True once the contract has left Draft / Review: the lifecycle actions stay visible but disabled. */
  isLocked?: boolean;
  /** False off the Contract View, where there is no block on screen to scroll to. */
  canFocusBlock?: boolean;
  /** False for the broker view: hides the grade strip, its notes and the lens-consensus caption. */
  showEvalControls: boolean;
  /** Block id → name, for the scroll-to-block tooltip and inline `[block:<id>]` references. */
  blockNameById: Map<string, string>;
  /** Section id → label, for resolving inline `[section:<id>]` references. */
  sectionNameById?: Map<string, string>;
  /** Step key → label, for the lens chip and the "flagged by N lenses" tooltip. */
  stepLabelByKey?: Map<string, string>;
  /** Finding id → title, so a revealed duplicate can name the finding it was merged into. */
  findingTitleById?: Map<string, string>;
  /** check_id → check text, for naming the checklist check an audit finding answers. */
  checkTextById?: Map<string, string>;
  onToggleExpand: (findingId: string) => void;
  onFocusBlock: (blockId: string | undefined) => void;
  onGrade: (findingId: string, grade: EvalFindingGrade | null, note?: string) => void;
  /**
   * `resolution: null` reopens a resolved / dismissed finding. With a
   * resolution, `note` is the reviewer's reason — saved with the verdict, or
   * on its own later from the reason field.
   */
  onResolve?: (findingId: string, resolution: EvalFindingResolution | null, note?: string) => void;
}

/**
 * The "Re-run review" line a card carries for the re-check's latest verdict,
 * or `undefined` when it has nothing to say:
 *
 *  - Open finding: any verdict short of RESOLVED reads "Unresolved". An open
 *    finding carrying a RESOLVED verdict is one the re-check closed and a
 *    reviewer then reopened — the verdict stays on the row as history (the
 *    eval harness reads "AI closed it, a human disagreed"), but the reviewer
 *    has overruled it, so nothing to report — least of all "Unresolved" over
 *    a rationale arguing the finding is resolved.
 *  - Closed finding: only a verdict from a check that ran AFTER the closing
 *    counts, as a second opinion; the verdict that closed it (or one older
 *    than the user's own decision) is already told by the status line. A
 *    resolved card reads "Resolved" / "Unresolved". A dismissed card gets the
 *    reasoning alone, with no verdict word: the user said "not an issue", and
 *    "Unresolved" there would be the tool arguing back.
 */
const rerunReviewHeading = ({
  verdict,
  resolution,
  verdictPostdatesClose,
}: {
  verdict: EvalFinding['ai_resolution_verdict'];
  resolution: EvalFindingResolution | undefined;
  verdictPostdatesClose: boolean;
}): string | undefined => {
  if (!verdict) return undefined;
  if (!resolution) return verdict === 'RESOLVED' ? undefined : 'Re-run review: Unresolved';
  if (!verdictPostdatesClose) return undefined;
  if (resolution === 'DISMISSED') return 'Re-run review';
  return verdict === 'RESOLVED' ? 'Re-run review: Resolved' : 'Re-run review: Unresolved';
};

/**
 * One suggestion. Collapsed: tier icon + title, plus a status icon once it is
 * closed. Expanded: the lens that raised it, the description, a tinted
 * recommendation callout, and a footer row with the jump to the block on the
 * left and the lifecycle actions on the right. A closed (resolved /
 * dismissed) card dims its content and adds a status line — who closed it
 * and when — over one reason: a field until one is saved, then text.
 */
export const FindingCard: FC<FindingCardProps> = ({
  finding,
  isActive,
  isExpanded,
  isGrading,
  isResolving,
  isLocked = false,
  canFocusBlock = true,
  showEvalControls,
  blockNameById,
  sectionNameById,
  stepLabelByKey,
  findingTitleById,
  checkTextById,
  onToggleExpand,
  onFocusBlock,
  onGrade,
  onResolve,
}) => {
  const [noteDraft, setNoteDraft] = useState<string>(finding.grade_note ?? '');
  const [isEditingNote, setIsEditingNote] = useState(false);

  const tier = TIER_BY_SEVERITY[finding.severity];
  const TierIcon = TIER_ICON[tier];
  const tone = tierClasses[tier];

  // A finding raised independently by more than one lens carries the agreeing
  // step keys; show the consensus so reviewers can prioritise robust issues.
  const lensCount = finding.reported_by_steps?.length ?? 0;
  const lensTooltip = finding.reported_by_steps?.map((key) => stepLabelByKey?.get(key) ?? key).join(', ');
  const stepLabel = stepLabelByKey?.get(finding.step_key);
  const lensLabel = lensDisplayLabel(finding.step_key, stepLabel);
  const LensIcon = lensHint(finding.step_key, stepLabel).Icon;

  // Only rendered when the "Show duplicates" toggle is on: a finding the
  // consolidation pass merged into another. Dim it and name its canonical.
  const isDuplicate = Boolean(finding.superseded_by);
  const canonicalTitle = finding.superseded_by ? findingTitleById?.get(finding.superseded_by) : undefined;

  // Lifecycle state. A finding with no `resolution` is open.
  const resolution = finding.resolution ?? undefined;
  const isClosed = Boolean(resolution);
  const isAiResolved = isClosed && finding.resolution_source === 'AI';
  // The re-check judges closed findings too, so a verdict on a closed card is
  // either the one that closed it or a later second opinion. Later = stamped
  // after the closing; the re-check's own close stamps both with one clock,
  // so a tie is the closing verdict. Both timestamps are ISO strings from the
  // same clock, so they compare as text.
  const verdictPostdatesClose =
    isClosed &&
    Boolean(finding.ai_resolution_at && finding.resolved_at) &&
    (finding.ai_resolution_at ?? '') > (finding.resolved_at ?? '');
  const reviewHeading = rerunReviewHeading({
    verdict: finding.ai_resolution_verdict,
    resolution,
    verdictPostdatesClose,
  });
  const StatusIcon = resolution === 'DISMISSED' ? CircleX : CircleCheck;

  const checkText = checkTextById?.get(finding.check_id ?? '');
  const blockName = finding.block_id ? (blockNameById.get(finding.block_id) ?? 'block') : undefined;
  const hasMeta = Boolean(finding.check_id) || isDuplicate;
  const hasFooter = Boolean(finding.block_id) || Boolean(onResolve);

  const refOptions = { blockNameById, sectionNameById, onBlockClick: onFocusBlock };

  // "Dismissed 13 March 2026 at 12:56 by John Patrick" — the status line on
  // the expanded card, and the tooltip on the collapsed row's status icon.
  // The re-check's own closes read "Re-run review: Resolved <when>".
  const closedStamp = formatDateTime(finding.resolved_at ?? finding.ai_resolution_at);
  const closedBy = finding.resolved_by?.name;
  const lifecycleLine = resolution
    ? isAiResolved
      ? ['Re-run review: Resolved', closedStamp].filter(Boolean).join(' ')
      : [RESOLUTION_LABEL[resolution], closedStamp, closedBy ? `by ${closedBy}` : ''].filter(Boolean).join(' ')
    : undefined;
  const actionsDisabled = isResolving || isLocked;
  const lockedTitle = isLocked ? 'This contract can no longer be edited' : undefined;

  // One reason per closed finding. The AI's rationale is the reason when the
  // re-check closed it — unless a later check has since overwritten it with a
  // second opinion, which belongs under the re-run review line instead.
  const closingRationale = isAiResolved && !verdictPostdatesClose ? finding.ai_resolution_rationale : undefined;
  const savedReason =
    finding.resolution_note ??
    (closingRationale ? replaceBlockRefsWithNames(closingRationale, { blockNameById, sectionNameById }) : '');
  const [reasonDraft, setReasonDraft] = useState('');
  // A saved reason is final and reads as text; the field only shows while
  // there is none. Clear the draft after a reopen (the server nulls the note).
  useEffect(() => {
    setReasonDraft('');
  }, [resolution]);
  const showReasonField = isClosed && savedReason === '';
  const canSaveReason = Boolean(resolution) && reasonDraft.trim() !== '';

  const handleGradeClick = (grade: EvalFindingGrade) => {
    const nextGrade = finding.grade === grade ? null : grade;
    onGrade(finding.id, nextGrade, nextGrade ? noteDraft : undefined);
  };

  const handleNoteSave = () => {
    onGrade(finding.id, finding.grade ?? null, noteDraft);
    setIsEditingNote(false);
  };

  const handleNoteKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleNoteSave();
    }
    if (event.key === 'Escape') {
      setNoteDraft(finding.grade_note ?? '');
      setIsEditingNote(false);
    }
  };

  const saveReason = () => {
    if (!resolution || !canSaveReason) return;
    onResolve?.(finding.id, resolution, reasonDraft.trim());
  };

  const handleReasonKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      saveReason();
    }
    if (event.key === 'Escape') {
      setReasonDraft('');
      event.currentTarget.blur();
    }
  };

  return (
    <li data-testid={`pbqa-finding-${finding.id}`} className="px-4 pb-2">
      <div
        className={cn(
          'rounded-lg border bg-white transition-colors',
          isActive ? 'border-primary-300 ring-primary-100 ring-2' : 'border-gray-200',
          isDuplicate && 'opacity-60',
        )}
      >
        <button
          type="button"
          onClick={() => onToggleExpand(finding.id)}
          aria-expanded={isExpanded}
          data-testid={`pbqa-finding-toggle-${finding.id}`}
          className="flex w-full cursor-pointer items-start gap-2.5 px-3 py-2.5 text-left"
        >
          <TierIcon
            className={cn('mt-px size-4 shrink-0', isClosed ? 'text-gray-300' : tone.text)}
            role="img"
            aria-label={TIER_LABEL[tier]}
          />
          <span
            className={cn(
              'min-w-0 flex-1 text-[13px] leading-[18px]',
              isExpanded ? 'font-semibold' : 'font-medium',
              isClosed ? 'text-gray-400' : 'text-gray-900',
              !isExpanded && 'truncate',
            )}
            title={finding.title}
          >
            {finding.title}
          </span>
          {/* Closed rows carry their status; who closed it and when is the tooltip. */}
          <Show when={isClosed}>
            <Tooltip content={lifecycleLine} disabled={!lifecycleLine}>
              <span
                className="inline-flex shrink-0 text-gray-400"
                data-testid={`pbqa-finding-resolution-icon-${finding.id}`}
              >
                <StatusIcon className="size-4" aria-hidden />
              </span>
            </Tooltip>
          </Show>
          {isExpanded ? (
            <ChevronUp className="size-4 shrink-0 text-gray-400" aria-hidden />
          ) : (
            <ChevronDown className="size-4 shrink-0 text-gray-400" aria-hidden />
          )}
        </button>

        <Show when={isExpanded}>
          <div className="flex flex-col gap-3 px-3 pb-3">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pl-[26px]">
              <Show when={Boolean(lensLabel)}>
                <span
                  className={cn(
                    'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium',
                    isClosed ? 'bg-gray-50 text-gray-400' : 'bg-gray-100 text-gray-600',
                  )}
                  data-testid={`pbqa-finding-lens-${finding.id}`}
                >
                  <LensIcon className="size-3" aria-hidden />
                  {lensLabel}
                </span>
              </Show>
              <Show when={showEvalControls && lensCount > 1}>
                <span
                  className="text-[11px] text-gray-400"
                  title={lensTooltip ? `Flagged by: ${lensTooltip}` : undefined}
                >
                  {lensCount} lenses
                </span>
              </Show>
            </div>

            <Show when={hasMeta}>
              <div className="flex min-w-0 flex-col gap-0.5">
                {/*
                  Audit-derived findings name the check they answer, on one quiet
                  caption — the full wording lives in the tooltip and the
                  checklist section; the finding body is the main content.
                */}
                <Show when={Boolean(finding.check_id)}>
                  <span
                    className="min-w-0 truncate text-[11px] text-gray-500"
                    title={`${finding.check_id}${checkText ? ` — ${checkText}` : ''}`}
                    data-testid={`pbqa-finding-check-${finding.id}`}
                  >
                    Check {finding.check_id}
                    {checkText ? ` · ${checkText}` : ''}
                  </span>
                </Show>
                <Show when={isDuplicate}>
                  <span
                    className="truncate text-[11px] text-gray-500"
                    title={
                      canonicalTitle ? `Duplicate of: ${canonicalTitle}` : 'Marked as a duplicate by consolidation'
                    }
                    data-testid={`pbqa-finding-duplicate-${finding.id}`}
                  >
                    {canonicalTitle ? `Duplicate of: ${canonicalTitle}` : 'Duplicate'}
                  </span>
                </Show>
              </div>
            </Show>

            <p
              className={cn(
                'text-[13px] leading-relaxed whitespace-pre-wrap',
                isClosed ? 'text-gray-400' : 'text-gray-700',
              )}
            >
              {renderTextWithBlockRefs(finding.description, refOptions)}
            </p>

            <Show when={Boolean(finding.recommendation)}>
              <div className={cn('rounded-md px-3 py-2.5', isClosed ? 'bg-gray-50 text-gray-400' : tone.callout)}>
                <p
                  className={cn(
                    'text-[11px] font-semibold tracking-wide uppercase',
                    isClosed ? 'text-gray-400' : tone.calloutTitle,
                  )}
                >
                  Recommendation
                </p>
                <p className="mt-1 text-[13px] leading-relaxed">
                  {renderTextWithBlockRefs(finding.recommendation ?? '', refOptions)}
                </p>
              </div>
            </Show>

            {/*
              Closed: who closed it and when, over the one reason for closing —
              for both verdicts. Until a reason is saved it is a field: Enter
              submits (Shift+Enter for a new line), as does the button, which
              stays off while the field is empty. Saved, it reads as text.
            */}
            <Show when={isClosed && Boolean(onResolve)}>
              <div className="flex flex-col gap-2">
                <p
                  className="flex items-center gap-1.5 text-[13px] leading-[18px] font-semibold text-gray-900"
                  data-testid={`pbqa-finding-resolution-${finding.id}`}
                >
                  <StatusIcon
                    className={cn('size-4 shrink-0', resolution === 'DISMISSED' ? 'text-error-500' : 'text-teal-600')}
                    aria-hidden
                  />
                  {lifecycleLine}
                </p>
                <Show when={!showReasonField}>
                  <p
                    className="text-[13px] leading-relaxed whitespace-pre-wrap text-gray-700"
                    data-testid={`pbqa-finding-reason-text-${finding.id}`}
                  >
                    {savedReason}
                  </p>
                </Show>
                <Show when={showReasonField}>
                  <textarea
                    rows={2}
                    value={reasonDraft}
                    disabled={actionsDisabled}
                    onChange={(e) => setReasonDraft(e.target.value)}
                    onKeyDown={handleReasonKeyDown}
                    placeholder="Add a reason…"
                    aria-label="Reason"
                    data-testid={`pbqa-finding-reason-${finding.id}`}
                    className={cn(
                      'w-full resize-none rounded-md border border-gray-300 px-3 py-2 text-[13px] text-gray-900 placeholder:text-gray-400',
                      'focus:border-primary-500 focus:ring-primary-500 focus:ring-1 focus:outline-none',
                      actionsDisabled && 'cursor-not-allowed opacity-60',
                    )}
                  />
                  <div className="flex justify-end">
                    <Button
                      size="sm"
                      className="text-[13px]"
                      disabled={actionsDisabled || !canSaveReason}
                      title={lockedTitle}
                      data-testid={`pbqa-finding-reason-submit-${finding.id}`}
                      onClick={saveReason}
                    >
                      Submit
                    </Button>
                  </div>
                </Show>
              </div>
            </Show>

            {/*
              The re-check's verdict, where the card should carry it (see
              `rerunReviewHeading`): "Unresolved" on an open finding it left
              open, a second opinion on a finding a user closed. Partial and
              unclear verdicts are nuance for the reasoning underneath, which
              is what a broker asking "why didn't my edit clear this?" wants.
            */}
            <Show when={Boolean(reviewHeading)}>
              <div className="flex flex-col gap-1.5" data-testid={`pbqa-finding-ai-verdict-${finding.id}`}>
                <p className="flex items-center gap-1.5 text-[13px] leading-[18px] font-semibold text-gray-900">
                  <Radar className="size-4 shrink-0 text-violet-600" aria-hidden />
                  {reviewHeading}
                </p>
                <Show when={Boolean(finding.ai_resolution_rationale)}>
                  <p className="text-[13px] leading-relaxed whitespace-pre-wrap text-gray-500">
                    {renderTextWithBlockRefs(finding.ai_resolution_rationale ?? '', refOptions)}
                  </p>
                </Show>
              </div>
            </Show>

            {/*
              Footer: the jump to the block on the left, the lifecycle actions
              on the right — Dismiss / Resolve while open, Reopen once closed.
              Contract-wide findings have nothing to jump to.
            */}
            <Show when={hasFooter}>
              <div className="flex items-center justify-between gap-2 border-t border-gray-100 pt-3">
                <Show when={Boolean(finding.block_id)}>
                  <button
                    type="button"
                    disabled={!canFocusBlock}
                    onClick={() => onFocusBlock(finding.block_id)}
                    data-testid={`pbqa-finding-goto-${finding.id}`}
                    className={cn(
                      'inline-flex shrink-0 items-center gap-1.5 text-[13px] font-medium whitespace-nowrap',
                      canFocusBlock ? 'text-primary-600 hover:text-primary-700' : 'cursor-not-allowed text-gray-400',
                    )}
                    title={
                      canFocusBlock
                        ? blockName
                          ? `Scroll to “${blockName}” in the contract`
                          : 'Scroll to this block in the contract'
                        : 'Switch to Contract View to jump to this block'
                    }
                  >
                    <CornerDownLeft className="size-3.5" aria-hidden />
                    Scroll to Block
                  </button>
                </Show>
                <Show when={!finding.block_id}>
                  <span />
                </Show>
                <Show when={Boolean(onResolve) && !isClosed}>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="text-[13px] text-gray-700"
                      disabled={actionsDisabled}
                      title={lockedTitle}
                      data-testid={`pbqa-finding-dismiss-${finding.id}`}
                      onClick={() => onResolve?.(finding.id, 'DISMISSED')}
                    >
                      <CircleX aria-hidden />
                      Dismiss
                    </Button>
                    <Button
                      size="sm"
                      className="text-[13px]"
                      disabled={actionsDisabled}
                      title={lockedTitle}
                      data-testid={`pbqa-finding-resolve-${finding.id}`}
                      onClick={() => onResolve?.(finding.id, 'RESOLVED')}
                    >
                      <CircleCheck aria-hidden />
                      Resolve
                    </Button>
                  </div>
                </Show>
                <Show when={Boolean(onResolve) && isClosed}>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-[13px] text-gray-700"
                    disabled={actionsDisabled}
                    title={lockedTitle}
                    data-testid={`pbqa-finding-reopen-${finding.id}`}
                    onClick={() => onResolve?.(finding.id, null)}
                  >
                    <Undo2 aria-hidden />
                    Reopen
                  </Button>
                </Show>
              </div>
            </Show>

            <Show when={showEvalControls}>
              <>
                <div className="mt-1 flex items-center gap-2">
                  <span className="text-[10px] tracking-wide text-gray-400 uppercase">Grade</span>
                  <div className="inline-flex overflow-hidden rounded-md border border-gray-200">
                    {GRADE_OPTIONS.map((option, index) => {
                      const isSelected = finding.grade === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          disabled={isGrading}
                          title={option.label}
                          data-testid={`pbqa-finding-grade-${finding.id}-${option.value}`}
                          onClick={() => handleGradeClick(option.value)}
                          className={cn(
                            gradeSegmentClasses,
                            index > 0 && 'border-l border-gray-200',
                            isSelected
                              ? 'bg-gray-900 text-white'
                              : 'bg-transparent text-gray-600 hover:bg-gray-50 hover:text-gray-900',
                            isGrading && 'cursor-not-allowed opacity-60',
                          )}
                        >
                          {option.shortLabel}
                        </button>
                      );
                    })}
                  </div>
                  <Show when={!!finding.grade && !isEditingNote}>
                    <button
                      type="button"
                      onClick={() => setIsEditingNote(true)}
                      className="ml-auto shrink-0 text-[11px] text-gray-400 transition-colors hover:text-gray-600"
                    >
                      {finding.grade_note ? 'Edit note' : '+ note'}
                    </button>
                  </Show>
                </div>

                <Show when={Boolean(finding.grade_note) && !isEditingNote}>
                  <p className="truncate text-[11px] text-gray-600 italic" title={finding.grade_note}>
                    “{finding.grade_note}”
                  </p>
                </Show>

                <Show when={isEditingNote}>
                  <input
                    type="text"
                    value={noteDraft}
                    disabled={isGrading}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    onKeyDown={handleNoteKeyDown}
                    className={cn(quietInputClasses, isGrading && 'cursor-not-allowed opacity-60')}
                    placeholder="Grading note — Enter to save"
                    data-testid={`pbqa-finding-note-${finding.id}`}
                  />
                </Show>
              </>
            </Show>
          </div>
        </Show>
      </div>
    </li>
  );
};
